/**
 * Connected ASSIST affordance (#185) — the Alert → ASSIST half of the cross-link.
 *
 * Rendered inside an Urgent-Alert detail panel (fused hotspot / CAP alert). It
 * looks up the Assistance Requests linked to the SAME raw source record this alert
 * sits on, and surfaces a contextual jump to them. Per design-council PART IX it
 * renders NOTHING until at least one linked request resolves — no COP clutter on
 * the (common) case of an alert with no assistance raised against it.
 *
 * Read-only: it does not mutate the request, it just opens the existing
 * AssistanceRequestDetail modal (owned by the parent) for the most-actionable one.
 */
import { useEffect, useState } from 'react';
import type { AssistanceRequestListItem } from '@tsr/shared-types';
import { fetchAssistanceRequests } from './api';
import { sortAssistRows } from './AssistQueueList';
import { alertLinkLookup, type AlertSelectionKind } from './lib/alertAssistLink';

export function ConnectedAssistLink({
  alertKind,
  recordId,
  onOpen,
}: {
  /** Which Urgent-Alert detail panel is hosting this (sets the link type). */
  alertKind: AlertSelectionKind;
  /** The panel feature's id (== a linked assist's `incident_link_id`). */
  recordId: string | null | undefined;
  /** Open the assist detail modal for a request id (parent owns the modal). */
  onOpen: (requestId: string) => void;
}): JSX.Element | null {
  const [linked, setLinked] = useState<AssistanceRequestListItem[]>([]);

  const lookup = alertLinkLookup(alertKind, recordId);
  const lookupType = lookup?.incident_link_type;
  const lookupId = lookup?.incident_link_id;

  useEffect(() => {
    if (!lookupType || !lookupId) {
      setLinked([]);
      return undefined;
    }
    const ctl = new AbortController();
    void fetchAssistanceRequests(
      { incident_link_type: lookupType, incident_link_id: lookupId, limit: 20 },
      ctl.signal,
    )
      .then((r) => setLinked(sortAssistRows(r.requests)))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [lookupType, lookupId]);

  // Contextual gate: no linked requests → render nothing.
  if (linked.length === 0) return null;

  const primary = linked[0]!;
  const more = linked.length - 1;

  return (
    <div data-testid="connected-assist" className="space-y-1">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Connected ASSIST{linked.length > 1 ? ` (${linked.length})` : ''}
      </div>
      <button
        type="button"
        data-testid="connected-assist-btn"
        onClick={() => onOpen(primary.id)}
        className="flex w-full items-center gap-2 rounded border border-violet-500/50 bg-violet-600/15 px-2 py-1.5 text-left text-[12px] text-violet-100 hover:bg-violet-600/25"
      >
        <span aria-hidden className="text-violet-300">
          ⛑
        </span>
        <span className="min-w-0 flex-1 truncate">
          {primary.requestor_name}
          <span className="text-violet-300/80">
            {' · '}
            {primary.urgency} · {primary.status.replace(/_/g, ' ')}
          </span>
        </span>
        <span aria-hidden className="shrink-0 text-violet-300">
          →
        </span>
      </button>
      {more > 0 && (
        <ul className="space-y-0.5 pl-1">
          {linked.slice(1).map((req) => (
            <li key={req.id}>
              <button
                type="button"
                data-testid="connected-assist-more"
                onClick={() => onOpen(req.id)}
                className="w-full truncate rounded px-1 py-0.5 text-left text-[11px] text-slate-300 hover:bg-slate-800"
              >
                {req.requestor_name}{' '}
                <span className="text-slate-500">
                  · {req.urgency} · {req.status.replace(/_/g, ' ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
